import { Link, createFileRoute } from "@tanstack/react-router";
import { ExpandableIntro } from "@/components/expandable-intro";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { CasinoList } from "@/components/casino-list";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks, HowWeTested } from "@/components/eeat-blocks";
import { DepositSpeedInfographic } from "@/components/infographics";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/zonder-bkr";
import { TEST_IMAGE } from "@/content/images";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { getAuthor } from "@/lib/authors";
import { ogImageMeta } from "@/lib/og";
import { getPublicCasinos } from "@/lib/queries";
import { SITE } from "@/lib/site";

const PATH = "/online-casino-zonder-bkr";
const URL = `${SITE.url}${PATH}/`;

export const Route = createFileRoute("/online-casino-zonder-bkr")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: "Online Casino Zonder BKR 2026: Beste Casino's Zonder Kredietcheck" },
        { name: "description", content: guide.description },
        { property: "og:title", content: "Online Casino Zonder BKR 2026: Beste Casino's Zonder Kredietcheck" },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("zonder-bkr"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            datePublished: "2026-10-18",
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Online casino zonder BKR", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: ZonderBkrPage,
});

const CHECKS = [
  { label: "Leeftijd 18+", done: true },
  { label: "Identiteit", done: true },
  { label: "Cruks", done: true },
  { label: "Rekening op eigen naam", done: true },
  { label: "BKR-toets", done: false },
  { label: "Inkomenscheck bij aanmelden", done: false },
];

function ChecksInfographic() {
  return (
    <figure id="infographic-zonder-bkr-checks" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">
        Wat een legaal casino wel en niet controleert
      </figcaption>
      <ul className="mt-5 grid gap-3 sm:grid-cols-2">
        {CHECKS.map((c) => (
          <li key={c.label} className="flex items-center gap-3 rounded-lg border border-border bg-secondary/50 p-3 text-sm">
            <span className={c.done ? "flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground" : "flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground"}>
              {c.done ? "✓" : "✕"}
            </span>
            <span className="font-medium text-foreground">{c.label}</span>
          </li>
        ))}
      </ul>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">
        Kassacheck {SITE.name}, november 2026 · alle vergeleken aanbieders
      </p>
    </figure>
  );
}

const METHODS = [
  { label: "iDEAL", status: "Eigen geld", ok: true },
  { label: "Trustly", status: "Eigen geld", ok: true },
  { label: "Paysafecard", status: "Prepaid tegoed", ok: true },
  { label: "Debitkaart", status: "Eigen geld", ok: true },
  { label: "Creditcard", status: "Krediet, verboden", ok: false },
  { label: "Achteraf betalen", status: "Krediet, verboden", ok: false },
];

function CreditInfographic() {
  return (
    <figure id="infographic-zonder-bkr-krediet" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">
        Betaalmethoden: eigen geld of krediet?
      </figcaption>
      <ul className="mt-5 space-y-2">
        {METHODS.map((m) => (
          <li key={m.label} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm">
            <span className="font-medium text-foreground">{m.label}</span>
            <span className={m.ok ? "rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary" : "rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-semibold text-destructive"}>
              {m.status}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">
        Volgens de Wet Koa mogen legale casino&apos;s geen krediet verstrekken.
      </p>
    </figure>
  );
}

function FlowInfographic() {
  const steps = [
    { t: "Kies", d: "Aanbieder met NL-vergunning" },
    { t: "Gegevens", d: "Naam, adres en BSN" },
    { t: "Cruks", d: "Automatische controle" },
    { t: "Limieten", d: "Stortlimiet instellen" },
    { t: "Storten", d: "iDEAL met eigen geld" },
  ];
  return (
    <figure id="infographic-zonder-bkr-stappen" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">
        Zo speel je bij een casino zonder BKR-check
      </figcaption>
      <ol className="mt-5 grid gap-3 sm:grid-cols-5">
        {steps.map((s, i) => (
          <li key={s.t} className="rounded-lg border border-border bg-secondary/50 p-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              {i + 1}
            </span>
            <p className="mt-2 text-sm font-semibold text-foreground">{s.t}</p>
            <p className="mt-1 text-xs text-muted-foreground">{s.d}</p>
          </li>
        ))}
      </ol>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">
        Nergens een BKR-toets: alleen leeftijd, identiteit en Cruks.
      </p>
    </figure>
  );
}

function ZonderBkrPage() {
  const casinos = getPublicCasinos();
  const budget = new LinkBudget(PATH);

  const takeaways = [
    "Geen enkel legaal online casino in Nederland doet een BKR-toets bij registratie.",
    "Een BKR-codering houdt je niet tegen om een account te openen.",
    "Casino's controleren wel je leeftijd, identiteit en Cruks.",
    "Creditcards en achteraf betalen zijn verboden: je speelt altijd met eigen geld.",
    "Gokken zelf komt niet in het BKR, een niet-afgeloste lening wel.",
    "Heb je schulden? Loket Kansspel helpt gratis en anoniem.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Online casino zonder BKR</span>
      </nav>

      <h1 className="max-w-4xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
        Online casino zonder BKR: spelen zonder kredietcheck, uitgelegd en gecontroleerd
      </h1>

      <div className="mt-2 max-w-3xl sm:mt-6">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>
      <ExpandableIntro>{guide.description}</ExpandableIntro>

      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <section aria-labelledby="casinos">
            <h2 id="casinos" className="text-xl font-semibold text-foreground">
              Top 10 online casino&apos;s zonder BKR-toets
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Onze vaste vergelijking van vergunde aanbieders, gecontroleerd op registratie,
              kredietregels en betaalmethoden.
            </p>
            <div className="mt-4">
              <CasinoList casinos={casinos} />
            </div>
            <figure className="mt-6 overflow-hidden rounded-xl border border-border">
              <img
                src={TEST_IMAGE.src}
                alt={TEST_IMAGE.alt}
                width={1280}
                height={720}
                loading="lazy"
                decoding="async"
                className="aspect-[21/9] w-full object-cover"
              />
              <figcaption className="border-t border-border bg-secondary/50 px-4 py-2 text-xs text-muted-foreground">
                Beeld: redactie {SITE.name}
              </figcaption>
            </figure>
          </section>

          <div className="mt-10">
            <HowWeTested
              subject="de registratie en kredietregels zonder BKR"
              providers={10}
              period="november 2026"
            />
          </div>

          <div className="mt-10">
            <KeyTakeaways items={takeaways} />
          </div>

          <div className="mt-10">
            <FlowInfographic />
          </div>

          <section className="mt-12" aria-label="Alles over casino's zonder BKR">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14 grid gap-6">
            <ChecksInfographic />
            <CreditInfographic />
            <DepositSpeedInfographic />
          </div>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="spelen zonder BKR"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over casino&apos;s zonder BKR
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks authorSlug={guide.authorSlug} reviewerSlug={guide.reviewerSlug} updatedAt={guide.updatedAt} />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
